/**
 * The individual controls `<FilterBar>` assembles — one per filter type. None of them holds filter
 * state (§4.4); the only local state here is presentational — popover open, in-progress typing.
 */

import { useId, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faCheck, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { format, isValid, parseISO } from 'date-fns';
import type { DateRange } from 'react-day-picker';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  ANY_VALUE,
  decodeMultiValue,
  encodeMultiValue,
  optionsOf,
  switchValues,
  type DateFilterField,
  type DateRangeFilterField,
  type FilterValues,
  type MultiSelectFilterField,
  type SelectFilterField,
  type SwitchFilterField,
  type TextFilterField,
} from '@/lib/filters';
import { cn } from '@/lib/utils';
import { useI18n } from '@/lib/i18n';

/** The wire format for every date in a filter: `yyyy-MM-dd`, matching the URL and the API. */
const PARAM_DATE = 'yyyy-MM-dd';
/** How a date reads to someone scanning a filter bar. Unambiguous about the month. */
const DISPLAY_DATE = 'd MMM yyyy';
/** Keep direct year navigation broad without removing the calendar's existing future navigation. */
const CALENDAR_END_MONTH = new Date(new Date().getFullYear() + 100, 11);

/**
 * Option count above which a multi-select offers a search box. Roughly the point where a list stops
 * being scannable in the popover's height, so the reader is scrolling to find a known code rather
 * than browsing what is on offer.
 */
const SEARCH_FROM = 10;

function toDate(value: string | null): Date | undefined {
  if (value === null) {
    return undefined;
  }
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : undefined;
}

function toParam(date: Date | undefined): string | null {
  return date === undefined ? null : format(date, PARAM_DATE);
}

function display(value: string | null): string | null {
  const date = toDate(value);
  return date === undefined ? null : format(date, DISPLAY_DATE);
}

/** The id of a field's hint text, so the control can point at it with `aria-describedby`. */
function hintId(id: string, hint: string | undefined): string | undefined {
  return hint === undefined ? undefined : `${id}-hint`;
}

/** Label, control, optional hint — the same vertical rhythm for every filter type (§4.9). */
function Field({
  id,
  labelId,
  label,
  hint,
  required,
  children,
}: {
  id: string;
  /**
   * For a control that names itself with `aria-labelledby`. Only the switch: Radix renders it as a
   * `<button>`, and `<label for>` pointing at a button is not a label association (§4.9).
   */
  labelId?: string | undefined;
  label: string;
  hint?: string | undefined;
  required?: boolean | undefined;
  children: React.ReactNode;
}) {
  const { text } = useI18n();
  return (
    // min-w-32 still holds `dd MMM yyyy` and a chevron; gap-0.5 matches `ButtonGroupSelector`, which
    // shares this row and would otherwise sit its label at a different height.
    <div className="flex min-w-32 flex-col gap-0.5">
      <Label id={labelId} htmlFor={id} className="text-xs font-medium text-foreground">
        {text(label)}
        {required === true ? (
          <span className="text-foreground">
            *<span className="sr-only"> (required)</span>
          </span>
        ) : null}
      </Label>
      {children}
      {hint !== undefined ? (
        <p id={hintId(id, hint)} className="text-xs text-muted-foreground">
          {text(hint)}
        </p>
      ) : null}
    </div>
  );
}

export function TextFilter({
  field,
  value,
  onChange,
}: {
  field: TextFilterField;
  value: string | null;
  onChange: (value: string | null) => void;
}) {
  const id = useId();
  const { text } = useI18n();
  const [draft, setDraft] = useState(value ?? '');
  const [committed, setCommitted] = useState(value);

  // Adjust during render, not in an effect: the box must follow the URL changing underneath it.
  if (value !== committed) {
    setCommitted(value);
    setDraft(value ?? '');
  }

  /**
   * Stage on every keystroke, not on blur.
   *
   * Staging is not querying — `<FilterBar>` holds the draft until Query is pressed — but Query is
   * disabled until something is staged, and a disabled button takes no click and so never blurs the
   * box. Committing only on blur therefore deadlocked: type a value, press Query, nothing happens,
   * and the next render syncs the box back to the URL and the text vanishes.
   *
   * `committed` moves with it so the render-time sync below sees no disagreement — otherwise typing
   * a trailing space would be echoed back trimmed, mid-keystroke.
   */
  const commitText = (next: string) => {
    setDraft(next);
    const trimmed = next.trim();
    const value = trimmed === '' ? null : trimmed;
    setCommitted(value);
    onChange(value);
  };

  const commit = () => {
    commitText(draft);
  };

  return (
    <Field id={id} label={field.label} hint={field.hint} required={field.required}>
      <Input
        id={id}
        type="text"
        value={draft}
        aria-describedby={hintId(id, field.hint)}
        placeholder={text(field.placeholder ?? 'Type to filter')}
        onChange={(event) => {
          commitText(event.target.value);
        }}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          }
        }}
      />
    </Field>
  );
}

export function SelectFilter({
  field,
  value,
  pending = false,
  onChange,
}: {
  field: SelectFilterField;
  value: string | null;
  /** Choices still being fetched (§5.4). Disabled rather than empty — empty reads as "there are none". */
  pending?: boolean;
  onChange: (value: string | null) => void;
}) {
  const id = useId();
  const { text } = useI18n();
  const anyLabel = text(field.anyLabel ?? 'All');
  const options = optionsOf(field.options);

  return (
    <Field id={id} label={field.label} hint={field.hint} required={field.required}>
      <Select
        value={value ?? ANY_VALUE}
        disabled={pending}
        onValueChange={(next) => onChange(next === ANY_VALUE ? null : next)}
      >
        <SelectTrigger
          id={id}
          aria-describedby={hintId(id, field.hint)}
          aria-busy={pending}
          className="w-full"
        >
          {/* No matching item while loading, so show the raw value rather than the placeholder. */}
          {pending ? (value ?? anyLabel) : <SelectValue placeholder={anyLabel} />}
        </SelectTrigger>
        <SelectContent>
          {field.required === true ? null : <SelectItem value={ANY_VALUE}>{anyLabel}</SelectItem>}
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

/**
 * A two-state dataset filter. **Both states are written explicitly — off is a value, not an
 * absence** — because a missing param means "whatever this report defaults to" and a default can
 * change, so only an explicit value keeps §4.4's promise. The state word beside it is the label
 * doing its job (§4.10): "PPA lines included", not "PPA lines: on".
 */
export function SwitchFilter({
  field,
  value,
  onChange,
}: {
  field: SwitchFilterField;
  value: string | null;
  onChange: (value: string | null) => void;
}) {
  const id = useId();
  const labelId = `${id}-label`;
  const { on, off } = switchValues(field);
  const checked = value === on;

  return (
    <Field
      id={id}
      labelId={labelId}
      label={field.label}
      hint={field.hint}
      required={field.required}
    >
      {/* h-9 to match every select and date trigger in the bar; a switch is shorter and would float. */}
      <div className="flex h-9 items-center gap-2">
        <Switch
          id={id}
          checked={checked}
          aria-labelledby={labelId}
          aria-describedby={hintId(id, field.hint)}
          onCheckedChange={(next) => {
            onChange(next ? on : off);
          }}
        />
        <span className="text-sm">
          {checked ? (field.onLabel ?? 'On') : (field.offLabel ?? 'Off')}
        </span>
      </div>
    </Field>
  );
}

/**
 * Multi-select dropdown. Nothing checked means all values (null on the wire); selections serialize
 * as a comma-separated URL param so a shared link keeps every choice.
 */
export function MultiSelectFilter({
  field,
  value,
  pending = false,
  onChange,
}: {
  field: MultiSelectFilterField;
  value: string | null;
  /** The choices are still being fetched (§5.4). See {@link SelectFilter}. */
  pending?: boolean;
  onChange: (value: string | null) => void;
}) {
  const id = useId();
  const { text } = useI18n();
  const anyLabel = text(field.anyLabel ?? 'All');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const options = optionsOf(field.options);
  const selected = decodeMultiValue(value);
  const selectedSet = new Set(selected);

  // Search appears once a list is too long to scan, and not before: a box above four brands is
  // furniture. No per-report flag — the list's own length is the only thing that decides it.
  const searchable = options.length > SEARCH_FROM;
  const needle = search.trim().toLowerCase();
  const shown =
    searchable && needle !== ''
      ? options.filter(
          (option) =>
            option.label.toLowerCase().includes(needle)
            || option.value.toLowerCase().includes(needle),
        )
      : options;

  const toggle = (optionValue: string) => {
    const next = selectedSet.has(optionValue)
      ? selected.filter((v) => v !== optionValue)
      : [...selected, optionValue];
    // Option order, so the URL does not reshuffle per toggle. A selection absent from the list is
    // dropped here — which is why the control is disabled until the list arrives, not just empty.
    const ordered = options
      .map((option) => option.value)
      .filter((v) => next.includes(v));
    onChange(encodeMultiValue(ordered));
  };

  const clear = () => {
    onChange(null);
  };

  const summary =
    selected.length === 0
      ? anyLabel
      : selected.length === 1
        ? (options.find((o) => o.value === selected[0])?.label ?? selected[0])
        : text('{count} selected', { count: selected.length });

  return (
    <Field id={id} label={field.label} hint={field.hint} required={field.required}>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          // Reopening on last time's search would hide options the reader never filtered out.
          if (!next) setSearch('');
        }}
      >
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={pending}
            aria-describedby={hintId(id, field.hint)}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-busy={pending}
            aria-label={
              selected.length === 0
                ? `${field.label}: ${anyLabel}`
                : `${field.label}: ${selected.join(', ')}`
            }
            className={cn(
              'h-9 w-full min-w-36 justify-between gap-2 px-3 font-normal',
              selected.length === 0 && 'text-muted-foreground',
            )}
          >
            <span className="truncate">{summary}</span>
            <FontAwesomeIcon icon={faChevronDown} className="size-3 shrink-0 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0" align="start">
          <div className="flex items-center justify-between border-b px-3 py-2">
            <span className="text-xs font-medium text-muted-foreground">
              {selected.length === 0 ? anyLabel : text('{count} selected', { count: selected.length })}
            </span>
            {selected.length > 0 ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={clear}
              >
                {text('Clear')}
              </Button>
            ) : null}
          </div>
          {searchable ? (
            <div className="border-b p-2">
              <Input
                type="search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                }}
                placeholder={text('Search {field}', { field: text(field.label).toLowerCase() })}
                aria-label={text('Search {field}', { field: text(field.label) })}
                className="h-8"
              />
            </div>
          ) : null}
          <ScrollArea className="h-64">
            {shown.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                {text('Nothing matches “{term}”. Try a shorter search.', { term: search.trim() })}
              </p>
            ) : null}
            <ul role="listbox" aria-multiselectable className="p-1" aria-labelledby={id}>
              {shown.map((option) => {
                const checked = selectedSet.has(option.value);
                return (
                  <li key={option.value}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={checked}
                      onClick={() => {
                        toggle(option.value);
                      }}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm',
                        'hover:bg-accent hover:text-accent-foreground',
                        checked && 'bg-accent/50',
                      )}
                    >
                      <span
                        className={cn(
                          'flex size-4 shrink-0 items-center justify-center rounded-sm border',
                          checked
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-input',
                        )}
                        aria-hidden
                      >
                        {checked ? <FontAwesomeIcon icon={faCheck} className="size-2.5" /> : null}
                      </span>
                      <span className="truncate">{option.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </Field>
  );
}

/** The shared trigger for both date controls, so a single date and a range read identically. */
function DateTrigger({
  id,
  text,
  placeholder,
  label,
  describedBy,
}: {
  id: string;
  text: string | null;
  placeholder: string;
  label: string;
  describedBy: string | undefined;
}) {
  return (
    <PopoverTrigger asChild>
      <Button
        id={id}
        variant="outline"
        aria-describedby={describedBy}
        aria-label={text === null ? `${label}: none chosen` : `${label}: ${text}`}
        className={cn(
          'h-9 w-full justify-start gap-2 px-3 font-normal',
          text === null && 'text-muted-foreground',
        )}
      >
        <FontAwesomeIcon icon={faCalendarDays} className="size-4 text-muted-foreground" />
        <span className="truncate">{text ?? placeholder}</span>
      </Button>
    </PopoverTrigger>
  );
}

export function DateFilter({
  field,
  value,
  onChange,
}: {
  field: DateFilterField;
  value: string | null;
  onChange: (value: string | null) => void;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);

  return (
    <Field id={id} label={field.label} hint={field.hint} required={field.required}>
      <Popover open={open} onOpenChange={setOpen}>
        <DateTrigger
          id={id}
          label={field.label}
          text={display(value)}
          placeholder="Choose a date"
          describedBy={hintId(id, field.hint)}
        />
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            captionLayout="dropdown"
            navLayout="after"
            endMonth={CALENDAR_END_MONTH}
            defaultMonth={toDate(value)}
            selected={toDate(value)}
            onSelect={(date) => {
              onChange(toParam(date));
              setOpen(false);
            }}
            autoFocus
          />
        </PopoverContent>
      </Popover>
    </Field>
  );
}

export function DateRangeFilter({
  field,
  values,
  onChange,
}: {
  field: DateRangeFilterField;
  values: FilterValues;
  onChange: (changes: Partial<FilterValues>) => void;
}) {
  const id = useId();
  const { text: translate } = useI18n();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange | undefined>();
  const [selectingEnd, setSelectingEnd] = useState(false);

  const from = values[field.fromParam] ?? null;
  const to = values[field.toParam] ?? null;

  const committed: DateRange | undefined =
    from === null && to === null ? undefined : { from: toDate(from), to: toDate(to) };

  const fromText = display(from);
  const toText = display(to);
  const text =
    fromText === null && toText === null
      ? null
      : `${fromText ?? 'Any'} – ${toText ?? 'Any'}`;

  return (
    <Field id={id} label={field.label} hint={field.hint} required={field.required}>
      <Popover
        open={open}
        onOpenChange={(nextOpen) => {
          if (nextOpen) {
            setDraft(committed);
            setSelectingEnd(false);
          }
          setOpen(nextOpen);
        }}
      >
        <DateTrigger
          id={id}
          label={field.label}
          text={text}
          placeholder="Choose a date range"
          describedBy={hintId(id, field.hint)}
        />
        <PopoverContent className="w-auto p-0" align="start">
          <div className="border-b px-3 py-2 text-sm text-muted-foreground" aria-live="polite">
            {selectingEnd && draft?.from !== undefined
              ? translate('Select an end date on or after {date}.', {
                  date: format(draft.from, DISPLAY_DATE),
                })
              : translate('Select a new start date.')}
          </div>
          <Calendar
            mode="range"
            captionLayout="dropdown"
            navLayout="after"
            endMonth={CALENDAR_END_MONTH}
            defaultMonth={toDate(from) ?? toDate(to)}
            selected={draft}
            onDayClick={(day) => {
              if (!selectingEnd || draft?.from === undefined) {
                setDraft({ from: day, to: undefined });
                setSelectingEnd(true);
                return;
              }

              if (day < draft.from) {
                setDraft({ from: day, to: undefined });
                return;
              }

              // Commit both ends together, so the URL never contains a half-changed range.
              onChange({
                [field.fromParam]: toParam(draft.from),
                [field.toParam]: toParam(day),
              });
              setOpen(false);
            }}
            autoFocus
          />
        </PopoverContent>
      </Popover>
    </Field>
  );
}
